/**
 * Domains — manage custom domains across all user sites.
 *
 * Features:
 * - Add domain form (hostname + site selector)
 * - List of existing domains with status, DNS instructions, and actions
 * - Auto-polls domains with pending_dns or validating status every 10 seconds
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { StatusBadge } from '../components/StatusBadge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type { Domain, Site } from '../types.ts';

/** Check whether any domain is still in a transitional state. */
const hasPendingDomain = (domains: Domain[]): boolean =>
  domains.some((d) => d.status === 'pending_dns' || d.status === 'validating');

export const Domains = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [hostname, setHostname] = useState('');
  const [siteId, setSiteId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  // -----------------------------------------------------------------------
  // Queries
  // -----------------------------------------------------------------------

  const {
    data: sitesResponse,
    isLoading: sitesLoading,
  } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.sites.list(),
  });

  const sites: Site[] = sitesResponse?.data ?? [];

  const {
    data: domainsResponse,
    isLoading: domainsLoading,
    error: domainsError,
  } = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.domains.list(),
    refetchInterval: (query) => {
      const domains: Domain[] = query.state.data?.data ?? [];
      return hasPendingDomain(domains) ? 10000 : false;
    },
  });

  const domains: Domain[] = domainsResponse?.data ?? [];

  // -----------------------------------------------------------------------
  // Mutations
  // -----------------------------------------------------------------------

  const addMutation = useMutation({
    mutationFn: () =>
      api.domains.add({ hostname: hostname.trim().toLowerCase(), siteId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      setHostname('');
      setSiteId('');
      setFormError(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (domainHostname: string) =>
      api.domains.remove(domainHostname),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      setConfirmRemove(null);
    },
  });

  const checkStatusMutation = useMutation({
    mutationFn: (domainHostname: string) =>
      api.domains.getStatus(domainHostname),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
    },
  });

  // -----------------------------------------------------------------------
  // Form validation & submit
  // -----------------------------------------------------------------------

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedHostname = hostname.trim().toLowerCase();

    if (!trimmedHostname) {
      setFormError(t('domains.validation.hostnameRequired'));
      return;
    }

    // Basic hostname validation
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(trimmedHostname)) {
      setFormError(t('domains.validation.hostnameInvalid'));
      return;
    }

    if (!siteId) {
      setFormError(t('domains.validation.siteRequired'));
      return;
    }

    setFormError(null);
    addMutation.mutate();
  };

  /** Find the site subdomain for a given site ID. */
  const getSiteSubdomain = (targetSiteId: string): string => {
    const site = sites.find((s) => s.id === targetSiteId);
    return site ? site.subdomain : targetSiteId;
  };

  // -----------------------------------------------------------------------
  // Loading & error states
  // -----------------------------------------------------------------------

  if (domainsLoading && sitesLoading) {
    return (
      <div className="loading-page">
        <div className="spinner spinner--lg" aria-label={t('domains.loading')} />
        <span>{t('domains.loading')}</span>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-header__title">{t('domains.title')}</h2>
          <p className="page-header__description">
            {t('domains.description')}
          </p>
        </div>
      </div>

      {/* Add domain form */}
      <div className="card mb-6" style={{ maxWidth: '600px' }}>
        <div className="card__header">
          <h3 className="card__title">{t('domains.addForm.title')}</h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="card__body">
            {addMutation.error && (
              <div className="alert alert--error" role="alert">
                {addMutation.error instanceof Error
                  ? addMutation.error.message
                  : t('domains.addForm.addError')}
              </div>
            )}

            {formError && (
              <div className="alert alert--error" role="alert">
                {formError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="hostname" className="form-label">
                {t('domains.addForm.hostname')}
              </label>
              <input
                id="hostname"
                type="text"
                className="form-input"
                value={hostname}
                onChange={(e) => {
                  setHostname(e.target.value);
                  if (formError) setFormError(null);
                }}
                placeholder={t('domains.addForm.hostnamePlaceholder')}
                aria-describedby="hostname-helper"
              />
              <p id="hostname-helper" className="form-helper">
                {t('domains.addForm.hostnameHelper')}
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="siteId" className="form-label">
                {t('domains.addForm.attachToSite')}
              </label>
              <select
                id="siteId"
                className="form-select"
                value={siteId}
                onChange={(e) => {
                  setSiteId(e.target.value);
                  if (formError) setFormError(null);
                }}
              >
                <option value="">{t('domains.addForm.selectSite')}</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.subdomain}.refineweb.site
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className="card__footer"
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <button
              type="submit"
              className="btn btn--primary"
              disabled={addMutation.isPending || sitesLoading}
            >
              {addMutation.isPending ? (
                <>
                  <span className="spinner" aria-hidden="true" />
                  {t('domains.addForm.adding')}
                </>
              ) : (
                t('domains.addForm.submit')
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Domain list */}
      <div className="card">
        <div className="card__header">
          <h3 className="card__title">{t('domains.list.title')}</h3>
        </div>

        {domainsLoading ? (
          <div className="card__body">
            <div className="loading-page" style={{ minHeight: '120px' }}>
              <div className="spinner" aria-label={t('domains.loading')} />
              <span>{t('domains.loading')}</span>
            </div>
          </div>
        ) : domainsError ? (
          <div className="card__body">
            <div className="alert alert--error" role="alert">
              {t('domains.list.loadError', { error: domainsError instanceof Error ? domainsError.message : t('common.error') })}
            </div>
          </div>
        ) : domains.length === 0 ? (
          <div className="card__body">
            <EmptyState
              title={t('domains.list.empty.title')}
              description={t('domains.list.empty.description')}
            />
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('domains.list.hostname')}</th>
                  <th>{t('domains.list.site')}</th>
                  <th>{t('domains.list.status')}</th>
                  <th>{t('domains.list.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {domains.map((domain) => (
                  <DomainRow
                    key={domain.hostname}
                    domain={domain}
                    siteSubdomain={getSiteSubdomain(domain.siteId)}
                    isConfirmingRemove={confirmRemove === domain.hostname}
                    onConfirmRemove={() => setConfirmRemove(domain.hostname)}
                    onCancelRemove={() => setConfirmRemove(null)}
                    onRemove={() => removeMutation.mutate(domain.hostname)}
                    onCheckStatus={() =>
                      checkStatusMutation.mutate(domain.hostname)
                    }
                    isRemoving={removeMutation.isPending}
                    isCheckingStatus={checkStatusMutation.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DomainRow — single row in the domain list table
// ---------------------------------------------------------------------------

interface DomainRowProps {
  domain: Domain;
  siteSubdomain: string;
  isConfirmingRemove: boolean;
  onConfirmRemove: () => void;
  onCancelRemove: () => void;
  onRemove: () => void;
  onCheckStatus: () => void;
  isRemoving: boolean;
  isCheckingStatus: boolean;
}

const DomainRow = ({
  domain,
  siteSubdomain,
  isConfirmingRemove,
  onConfirmRemove,
  onCancelRemove,
  onRemove,
  onCheckStatus,
  isRemoving,
  isCheckingStatus,
}: DomainRowProps) => {
  const { t } = useTranslation();

  return (
    <>
      <tr>
        <td>
          <strong style={{ fontSize: 'var(--text-sm)' }}>
            {domain.hostname}
          </strong>
        </td>
        <td>{siteSubdomain}.refineweb.site</td>
        <td>
          <StatusBadge status={domain.status} />
        </td>
        <td>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {(domain.status === 'pending_dns' ||
              domain.status === 'validating') && (
              <button
                className="btn btn--secondary btn--sm"
                onClick={onCheckStatus}
                disabled={isCheckingStatus}
                type="button"
              >
                {isCheckingStatus ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    {t('domains.list.checking')}
                  </>
                ) : (
                  t('domains.list.checkStatus')
                )}
              </button>
            )}
            {isConfirmingRemove ? (
              <>
                <button
                  className="btn btn--danger btn--sm"
                  onClick={onRemove}
                  disabled={isRemoving}
                  type="button"
                >
                  {isRemoving ? t('common.removing') : t('common.confirm')}
                </button>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={onCancelRemove}
                  type="button"
                >
                  {t('common.cancel')}
                </button>
              </>
            ) : (
              <button
                className="btn btn--ghost btn--sm"
                onClick={onConfirmRemove}
                type="button"
                style={{ color: 'var(--color-danger-600)' }}
              >
                {t('common.remove')}
              </button>
            )}
          </div>
        </td>
      </tr>
      {domain.status === 'pending_dns' && (
        <tr>
          <td colSpan={4} style={{ padding: 0 }}>
            <div style={{ padding: '0 var(--space-4) var(--space-4)' }}>
              <div className="dns-panel">
                <div className="dns-panel__title">
                  {t('domains.dns.instructions')}
                </div>
                <div className="dns-panel__record">
                  {t('domains.dns.cname', { hostname: domain.hostname })}
                </div>
                <div className="dns-panel__record">
                  {t('domains.dns.txt', { hostname: domain.hostname, validationRecords: domain.validationRecords ?? t('domains.dns.loadingRecords') })}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
